import { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, User, Phone, MapPin, CheckCircle, XCircle, AlertCircle, Mail, CalendarDays, ChevronLeft, ChevronRight, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { apiRequest, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  pending_confirmation: { label: 'Awaiting Client', color: 'bg-orange-100 text-orange-700', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const TIME_SLOTS = [
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM'
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState(false);
  const [offerModal, setOfferModal] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmTime, setConfirmTime] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [isConfirming, setIsConfirming] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const fetchAppointments = useCallback(async () => {
    try {
      const [allAppts, calAppts] = await Promise.all([
        apiRequest('/admin/appointments'),
        apiRequest('/admin/appointments/calendar')
      ]);
      setAppointments(allAppts);
      setCalendarAppointments(calAppts);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusChange = async (apptId, newStatus) => {
    try {
      await apiRequest(`/admin/appointments/${apptId}/status?status=${newStatus}`, { method: 'PUT' });
      setAppointments(appointments.map(a => 
        a.id === apptId ? { ...a, status: newStatus } : a
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openConfirmModal = (appt) => {
    setSelectedAppt(appt);
    // Pre-fill with offered date/time if available
    if (appt.offered_date) {
      setConfirmDate(appt.offered_date);
      setConfirmTime(appt.offered_time || '9:00 AM');
    } else {
      const today = new Date();
      setConfirmDate(today.toISOString().split('T')[0]);
      setConfirmTime('9:00 AM');
    }
    setSendEmail(true);
    setConfirmModal(true);
  };

  const openOfferModal = (appt) => {
    setSelectedAppt(appt);
    const today = new Date();
    setConfirmDate(today.toISOString().split('T')[0]);
    setConfirmTime('9:00 AM');
    setAdminNotes('');
    setSendEmail(true);
    setOfferModal(true);
  };

  const handleOffer = async () => {
    if (!confirmDate || !confirmTime) {
      toast.error('Please select date and time');
      return;
    }
    
    setIsConfirming(true);
    try {
      const updated = await apiRequest(`/admin/appointments/${selectedAppt.id}/offer`, {
        method: 'PUT',
        body: JSON.stringify({
          offered_date: confirmDate,
          offered_time: confirmTime,
          notes: adminNotes,
          send_email: sendEmail
        })
      });
      
      setAppointments(appointments.map(a => 
        a.id === selectedAppt.id ? updated : a
      ));
      
      toast.success('Offer sent! Client will receive an email to confirm.');
      setOfferModal(false);
    } catch (error) {
      toast.error('Failed to send offer');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleConfirm = async () => {
    if (!confirmDate || !confirmTime) {
      toast.error('Please select date and time');
      return;
    }
    
    setIsConfirming(true);
    try {
      const updated = await apiRequest(`/admin/appointments/${selectedAppt.id}/confirm`, {
        method: 'PUT',
        body: JSON.stringify({
          confirmed_date: confirmDate,
          confirmed_time: confirmTime,
          send_email: sendEmail
        })
      });
      
      setAppointments(appointments.map(a => 
        a.id === selectedAppt.id ? updated : a
      ));
      setCalendarAppointments([...calendarAppointments, updated]);
      
      toast.success(sendEmail ? 'Appointment confirmed and email sent!' : 'Appointment confirmed!');
      setConfirmModal(false);
    } catch (error) {
      toast.error('Failed to confirm appointment');
    } finally {
      setIsConfirming(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getAppointmentsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendarAppointments.filter(a => a.confirmed_date === dateStr);
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const filteredAppointments = filter === 'all' 
    ? appointments 
    : appointments.filter(a => a.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-appointments">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Appointments
          </h1>
          <p className="text-slate-500 mt-1">{appointments.length} appointment requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            onClick={() => setView('list')}
            className={view === 'list' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            List View
          </Button>
          <Button
            variant={view === 'calendar' ? 'default' : 'outline'}
            onClick={() => setView('calendar')}
            className={view === 'calendar' ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            <CalendarDays className="w-4 h-4 mr-2" />
            Calendar
          </Button>
        </div>
      </div>

      {view === 'calendar' ? (
        /* Calendar View */
        <Card className="border-none shadow-sm">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={prevMonth}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <CardTitle className="text-xl">
                {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </CardTitle>
              <Button variant="ghost" onClick={nextMonth}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {DAYS_OF_WEEK.map(day => (
                <div key={day} className="text-center py-2 text-sm font-medium text-slate-500">
                  {day}
                </div>
              ))}
              {/* Calendar days */}
              {getDaysInMonth(currentMonth).map((day, idx) => {
                const dayAppointments = getAppointmentsForDay(day);
                const isToday = day && new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[100px] p-1 border rounded-lg ${
                      day ? 'bg-white hover:bg-slate-50' : 'bg-slate-50'
                    } ${isToday ? 'border-orange-500 border-2' : 'border-slate-200'}`}
                  >
                    {day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-orange-600' : 'text-slate-700'}`}>
                          {day}
                        </div>
                        <div className="space-y-1">
                          {dayAppointments.slice(0, 3).map(appt => (
                            <div
                              key={appt.id}
                              className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded truncate"
                              title={`${appt.confirmed_time} - ${appt.name}`}
                            >
                              {appt.confirmed_time} {appt.name.split(' ')[0]}
                            </div>
                          ))}
                          {dayAppointments.length > 3 && (
                            <div className="text-xs text-slate-500">
                              +{dayAppointments.length - 3} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* List View */
        <>
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {['all', 'pending', 'pending_confirmation', 'confirmed', 'completed', 'cancelled'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-orange-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border'
                }`}
                data-testid={`filter-${status}`}
              >
                {status === 'pending_confirmation' ? 'Awaiting Client' : status.charAt(0).toUpperCase() + status.slice(1)}
                {status !== 'all' && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs bg-white/20">
                    {appointments.filter(a => a.status === status).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Appointments Grid */}
          {filteredAppointments.length === 0 ? (
            <Card className="border-none shadow-sm">
              <CardContent className="py-16 text-center">
                <p className="text-slate-500">No appointments found</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAppointments.map((appt) => {
                const statusConfig = STATUS_CONFIG[appt.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                
                return (
                  <Card key={appt.id} className="border-none shadow-sm hover:shadow-md transition-shadow" data-testid={`appt-card-${appt.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{appt.name}</CardTitle>
                          <p className="text-sm text-slate-500">{appt.service_needed}</p>
                        </div>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{appt.phone}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="truncate">{appt.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        <span>{appt.city}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <span>{formatDate(appt.created_at)}</span>
                      </div>

                      {/* Offered Date/Time (awaiting client confirmation) */}
                      {appt.status === 'pending_confirmation' && appt.offered_date && (
                        <div className="pt-3 border-t bg-orange-50 -mx-4 px-4 py-2 -mb-4 rounded-b-lg">
                          <p className="text-sm font-medium text-orange-800">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Offered: {appt.offered_date} at {appt.offered_time}
                          </p>
                          <p className="text-xs text-orange-600 mt-1">Awaiting client confirmation</p>
                        </div>
                      )}

                      {/* Confirmed Date/Time */}
                      {appt.status === 'confirmed' && appt.confirmed_date && (
                        <div className="pt-3 border-t bg-green-50 -mx-4 px-4 py-2 -mb-4 rounded-b-lg">
                          <p className="text-sm font-medium text-green-800">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Confirmed: {appt.confirmed_date} at {appt.confirmed_time}
                          </p>
                          {appt.client_confirmed_at && (
                            <p className="text-xs text-green-600 mt-1">Client confirmed</p>
                          )}
                        </div>
                      )}

                      {/* Preferred Times */}
                      {appt.status === 'pending' && appt.preferred_times && appt.preferred_times.length > 0 && (
                        <div className="pt-3 border-t">
                          <p className="text-xs text-slate-500 mb-2">Preferred Times:</p>
                          <div className="flex flex-wrap gap-1">
                            {appt.preferred_times.slice(0, 3).map((time, idx) => (
                              <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                                {time.split(' - ')[0]}
                              </span>
                            ))}
                            {appt.preferred_times.length > 3 && (
                              <span className="text-xs text-slate-400">+{appt.preferred_times.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="pt-3 border-t flex gap-2">
                        {appt.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                              onClick={() => openOfferModal(appt)}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Offer Time
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1"
                              onClick={() => openConfirmModal(appt)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Direct Confirm
                            </Button>
                          </>
                        )}
                        {appt.status === 'pending_confirmation' && (
                          <>
                            <Button
                              size="sm"
                              className="flex-1 bg-green-600 hover:bg-green-700"
                              onClick={() => openConfirmModal(appt)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Force Confirm
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openOfferModal(appt)}
                            >
                              Resend
                            </Button>
                          </>
                        )}
                        {appt.status === 'confirmed' && (
                          <Button
                            size="sm"
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleStatusChange(appt.id, 'completed')}
                          >
                            Mark Complete
                          </Button>
                        )}
                        {(appt.status === 'pending' || appt.status === 'pending_confirmation') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleStatusChange(appt.id, 'cancelled')}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Confirmation Modal */}
      <Dialog open={confirmModal} onOpenChange={setConfirmModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Appointment</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{selectedAppt.name}</p>
                <p className="text-sm text-slate-500">{selectedAppt.service_needed}</p>
                <p className="text-sm text-slate-500">{selectedAppt.phone}</p>
              </div>

              {selectedAppt.preferred_times?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Client's Preferred Times:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedAppt.preferred_times.map((time, idx) => (
                      <span key={idx} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {time}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="confirm-date">Date</Label>
                  <input
                    type="date"
                    id="confirm-date"
                    value={confirmDate}
                    onChange={(e) => setConfirmDate(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-time">Time</Label>
                  <Select value={confirmTime} onValueChange={setConfirmTime}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Send confirmation email</p>
                  <p className="text-xs text-slate-500">Notify client of confirmed time</p>
                </div>
                <Switch checked={sendEmail} onCheckedChange={setSendEmail} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmModal(false)}>Cancel</Button>
            <Button 
              onClick={handleConfirm} 
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700"
            >
              {isConfirming ? 'Confirming...' : 'Confirm Appointment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Offer Time Modal */}
      <Dialog open={offerModal} onOpenChange={setOfferModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Offer Appointment Time</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{selectedAppt.name}</p>
                <p className="text-sm text-slate-500">{selectedAppt.service_needed}</p>
                <p className="text-sm text-slate-500">{selectedAppt.email}</p>
              </div>

              {selectedAppt.preferred_times?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Client's Preferred Times:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedAppt.preferred_times.map((time, idx) => (
                      <span key={idx} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        {time}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  The client will receive an email with this time and must confirm to finalize the appointment.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="offer-date">Offered Date</Label>
                  <input
                    type="date"
                    id="offer-date"
                    value={confirmDate}
                    onChange={(e) => setConfirmDate(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <Label htmlFor="offer-time">Offered Time</Label>
                  <Select value={confirmTime} onValueChange={setConfirmTime}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="admin-notes">Notes for Client (optional)</Label>
                <Textarea
                  id="admin-notes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Any additional notes for the client..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Send email to client</p>
                  <p className="text-xs text-slate-500">Client will receive confirmation request</p>
                </div>
                <Switch checked={sendEmail} onCheckedChange={setSendEmail} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOfferModal(false)}>Cancel</Button>
            <Button 
              onClick={handleOffer} 
              disabled={isConfirming}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {isConfirming ? 'Sending...' : (
                <>
                  <Send className="w-4 h-4 mr-1" />
                  Send Offer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
